import type { PaymentContext, PaymentHandler, PaymentMethodConfig } from "../types/payment";
import { createCASHHandler } from "./handlers/cashHandler";
import { createPaymentTrackerHandler } from "./handlers/paymentTrackerHandler";
import { createSBPHandler } from "./handlers/sbpHandler";
import { createTerminalHandler } from "./handlers/terminalHandler";
import { createYandexBadgeHandler } from "./handlers/yandexBadgeHandler";
import { createYoukassaHandler } from "./handlers/youkassaHandler";

/**
 * Регистр методов оплаты
 * Централизованное место для регистрации и управления методами оплаты
 */

// biome-ignore lint/complexity/noStaticOnlyClass: Correct way to create a static class
export class PaymentMethodRegistry {
	private static methods: Map<string, PaymentMethodConfig> = new Map();

	/**
	 * Инициализация регистра с методами оплаты по умолчанию
	 */
	static initialize(): void {
		PaymentMethodRegistry.register({
			id: "TERMINAL_BASE",
			method: "TERMINAL",
			handlerFactory: createTerminalHandler,
			availableContexts: ["kiosk"],
		});

		PaymentMethodRegistry.register({
			id: "YOOKASSA_BASE",
			method: "YOOKASSA",
			handlerFactory: createYoukassaHandler,
			availableContexts: ["qr", "tableservice"],
		});

		PaymentMethodRegistry.register({
			id: "SBP",
			method: "YOOKASSA",
			handlerFactory: createSBPHandler,
			availableContexts: ["kiosk"],
		});

		PaymentMethodRegistry.register({
			id: "CASH",
			method: "CASH",
			handlerFactory: createCASHHandler,
			availableContexts: ["kiosk"],
		});

		PaymentMethodRegistry.register({
			id: "YANDEX_BADGE",
			method: "YANDEX_BADGE",
			handlerFactory: createYandexBadgeHandler,
			availableContexts: ["kiosk"],
		});

		PaymentMethodRegistry.register({
			id: "PAYMENT_TRACKER",
			method: "PAYMENT_TRACKER",
			handlerFactory: createPaymentTrackerHandler,
			availableContexts: ["kiosk"],
		});
	}

	static register(config: PaymentMethodConfig): void {
		PaymentMethodRegistry.methods.set(config.id, config);
	}

	static getConfig(id: string): PaymentMethodConfig | null {
		return PaymentMethodRegistry.methods.get(id) ?? null;
	}

	/** Получение доступных методов оплаты для контекста (возвращает id) */
	static getAvailableMethods(context: PaymentContext): string[] {
		return Array.from(PaymentMethodRegistry.methods.entries())
			.filter(([_, config]) => config.availableContexts.includes(context))
			.map(([id]) => id);
	}

	/** Проверка доступности метода для контекста  */
	static isMethodAvailable(id: string, context: PaymentContext): boolean {
		const config = PaymentMethodRegistry.getConfig(id);
		return config ? config.availableContexts.includes(context) : false;
	}

	static createHandler(id: string): PaymentHandler | null {
		const config = PaymentMethodRegistry.getConfig(id);
		if (!config) {
			console.warn(`PaymentMethodRegistry: метод оплаты ${id} не зарегистрирован`);
			return null;
		}
		return config.handlerFactory();
	}
}

PaymentMethodRegistry.initialize();
